import { api } from './client';

const BASE = '/admin/browser-profiles';

export interface BrowserWorkspace {
  id: number;
  name: string;
  profiles: number;
  created_at: number;
}

export type BrowserScheme = 'none' | 'firebase';

export const SCHEME_LABEL: Record<BrowserScheme, string> = {
  none: 'None — opened by hand',
  firebase: 'Firebase — new projects for apps',
};

// A Google account with a ShardX profile that is not a Play publishing
// account. Secrets come back only as presence flags.
export interface BrowserProfile {
  id: number;
  workspace_id: number;
  name: string;
  scheme: BrowserScheme;
  notes: string | null;
  enabled: number;
  google_email: string | null;
  shardx_profile_id: string | null;
  session_status: string;
  last_keepalive_at: number | null;
  check_requested_at: number | null;
  deleted_at: number | null;
  created_at: number;
  has_session_evidence: boolean;
  has_password: boolean;
  has_totp: boolean;
  backup_codes_left: number;
  recovery_email: string;
  proxy_label: string;
  cookies_pending: boolean;
  platform: string;
  has_app_password: boolean;
  firebase_max_projects: number;
  firebase_projects: number;
  /** Apps whose Firebase project was made in this profile; state 'ready' = linked. */
  firebase_apps: Array<{ bundle_id: string; state: string; project_id: string | null; error: string | null }>;
  // Shared Play Console: "Check invite" and the Play account linked to it.
  play_link_requested_at: number | null;
  play_link_started_at: number | null;
  play_link_error: string | null;
  play_link: { developers: Array<{ id: string; name: string | null }>; accepted: boolean; checked_at: number } | null;
  play_account_id: number | null;
  play_account_name: string | null;
}

export interface BrowserProfileInput {
  workspace_id?: number;
  name?: string;
  scheme?: BrowserScheme;
  notes?: string;
  enabled?: boolean;
  google_email?: string;
  password?: string;
  totp_seed?: string;
  backup_codes?: string[];
  recovery_email?: string;
  proxy?: string;
  cookies?: string;
  platform?: string;
  app_password?: string;
  firebase_max_projects?: number;
}

// One row for the bulk importer: a parsed account paired with a proxy.
export interface BulkProfileInput {
  name: string;
  google_email?: string;
  password?: string;
  recovery_email?: string;
  totp_seed?: string;
  app_password?: string;
  proxy: string;
  notes?: string;
}

export const listWorkspaces = () => api.get<BrowserWorkspace[]>(`${BASE}/workspaces`);
export const createWorkspace = (name: string) => api.post<{ id: number; name: string }>(`${BASE}/workspaces`, { name });
export const renameWorkspace = (id: number, name: string) => api.put<{ id: number }>(`${BASE}/workspaces/${id}`, { name });
export const deleteWorkspace = (id: number) => api.delete<{ id: number }>(`${BASE}/workspaces/${id}`);

export const listProfiles = () => api.get<BrowserProfile[]>(`${BASE}`);
export const createProfile = (input: BrowserProfileInput) => api.post<{ id: number }>(`${BASE}`, input);
export const updateProfile = (id: number, input: BrowserProfileInput) => api.put<{ id: number }>(`${BASE}/${id}`, input);
export const deleteProfile = (id: number) => api.delete<{ id: number; pending: boolean }>(`${BASE}/${id}`);
export const checkProfile = (id: number) => api.post<{ id: number }>(`${BASE}/${id}/check`);
export const bulkCreateProfiles = (input: { workspace_id: number; scheme: BrowserScheme; profiles: BulkProfileInput[] }) =>
  api.post<{ created: number; ids: number[] }>(`${BASE}/bulk`, input);
export const getProfileEvidence = (id: number) => api.blob(`${BASE}/${id}/evidence`);

// Shared Play Console: accept an invite in this profile's Google account, then
// link one developer it opens as a First publication account.
export const checkPlayInvite = (id: number) => api.post<{ id: number }>(`${BASE}/${id}/play-link`);
export const linkPlayAccount = (id: number, input: { developer_id: string; name?: string; contact_email?: string }) =>
  api.post<{ id: number }>(`${BASE}/${id}/play-account`, input);
